import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { SiteModel } from 'src/app/models/site.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import moment, { Moment } from 'moment';
import { SitesSelectorComponent } from '../../../shared/sites-selector/sites-selector.component';
import { TagsSelectorComponent } from '../../../shared/tags-selector/tags-selector.component';
import { RBACService } from '../../../shared/rbac.service';
import { TagModel } from '../../../models/tag.model';

declare var google;

@Component({
  selector: 'app-sites-details-edit',
  templateUrl: './sites-details-edit.component.html',
  styleUrls: ['./sites-details-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesDetailsEditComponent implements OnInit {
  inProgress: boolean = false;

  site: SiteModel = new SiteModel();

  site_risk_assessments: string[] = [
    'very low',
    'low',
    'moderate',
    'high',
    'critical'
  ];

  estimatedProjectStartDate: moment.Moment | null;
  estimatedProjectEndDate: moment.Moment | null;
  siteOpenTime: moment.Moment | null;
  siteCloseTime: moment.Moment | null;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle.
  canViewSites: boolean = false;
  canViewTags: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Resolve the RBAC permission flags used by the template before any early return.
    this.canViewSites = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_VIEW
      )
    ]);
    this.canViewTags = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_ACCOUNT_TAGS,
        RBACService.RBAC_A.A_VIEW
      )
    ]);

    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/sites']);
      return;
    }

    const parent_id: number = Number(
      this.route.parent.snapshot.params['parent_id']
    );
    const child_id: number = Number(
      this.route.parent.snapshot.params['child_id']
    );

    // Check if we are updating a site or creating a new one.
    if (
      child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        parent_id)
    ) {
      // Store the site id.
      this.site.id = child_id ? child_id : parent_id;
      // Get the data from the API.
      this.api
        .makeRequest('get', 'v1/sites/' + this.site.id)
        .then(
          (response: any): void => {
            this.site.apply(response.data);

            // Set the estimated project start date
            if (this.site.estimated_project_start_date) {
              this.estimatedProjectStartDate = moment.unix(
                this.site.estimated_project_start_date
              );
            }

            // Set the estimated project end date
            if (this.site.estimated_project_end_date) {
              this.estimatedProjectEndDate = moment.unix(
                this.site.estimated_project_end_date
              );
            }

            // Set the site opening time
            if (this.site.open_time) {
              this.siteOpenTime = this.toMoment(this.site.open_time);
            }

            // Set the site closing time
            if (this.site.close_time) {
              this.siteCloseTime = this.toMoment(this.site.close_time);
            }

            // Extract Tag ids.
            this.site.tag_ids = this.site.tags
              ? this.site.tags.map((tag: TagModel): number => tag.id)
              : [];
          }
        )
        .catch(
          (err: any): void => {
            this.utils.showModal('Error', err.error.message);
          }
        );
    } else {
      // Get and store the parent site id if it is present.
      if (parent_id) {
        // Store the site id.
        this.site.parent_site_id = parent_id;
      }

      // Set the site opening time
      if (this.site.open_time) {
        this.siteOpenTime = this.toMoment(this.site.open_time);
      }

      // Set the site closing time
      if (this.site.close_time) {
        this.siteCloseTime = this.toMoment(this.site.close_time);
      }

      // Load the Google Maps API (all libraries) then configure the address lookup.
      this.utils
        .loadGoogleMaps()
        .then(
          (): void => {
            // set the default geofence bounds and coordinates
            const initBounds = new google.maps.LatLngBounds();
            initBounds.extend(new google.maps.LatLng(-36.848552, 174.762105));

            // add address lookup
            const autocomplete = new google.maps.places.Autocomplete(
              document.getElementById('location')
            );
            autocomplete.setBounds(initBounds);
            autocomplete.addListener('place_changed', () => {
              // get the place
              const place = autocomplete.getPlace();
              // store the location
              this.site.location = place.formatted_address;
            });
          }
        )
        .catch(
          (): void => {
            this.utils.showModal(
              'Error',
              'The map could not be loaded. Please refresh the page and try again.'
            );
          }
        );
    }
  }

  /**
   * Get the output timezone value from the form control.
   * @param timezone
   */
  getOutputTimezone(timezone: string): void {
    this.site.timezone = timezone;
  }

  /**
   * Create or update the site.
   * @param form
   */
  onSubmit(form: NgForm): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (!form.valid) {
      this.utils.showModal(
        'Form Error',
        'Please make sure you enter all fields with valid information.'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    // Convert the estimated project start and end dates to a unix timestamp.
    if (
      this.estimatedProjectStartDate &&
      this.estimatedProjectStartDate instanceof moment
    ) {
      this.site.estimated_project_start_date = this.estimatedProjectStartDate.unix();
    } else {
      this.site.estimated_project_start_date = null;
    }

    if (
      this.estimatedProjectEndDate &&
      this.estimatedProjectEndDate instanceof moment
    ) {
      this.site.estimated_project_end_date = this.estimatedProjectEndDate.unix();
    } else {
      this.site.estimated_project_end_date = null;
    }

    // Convert the opening and closing time to a time string.
    if (this.siteOpenTime && this.siteOpenTime instanceof moment) {
      this.site.open_time = this.toTimeString(this.siteOpenTime);
    }

    if (this.siteCloseTime && this.siteCloseTime instanceof moment) {
      this.site.close_time = this.toTimeString(this.siteCloseTime);
    }

    // Check if the site should be created or updated.
    if (this.site.id) {
      this.api
        .makeRequest('put', 'v1/sites/' + this.site.id, this.site)
        .then(
          (response: any): void => {
            this.utils.showToast(
              'The ' +
                this.utils
                  .getLangTerm(
                    this.site.parent_site_id
                      ? 'child-sites.singular'
                      : 'sites.singular',
                    this.site.parent_site_id ? 'Subsite' : 'Site'
                  )
                  .toLowerCase() +
                ' was updated.'
            );
            this.inProgress = false;
          }
        )
        .catch(
          (err: any): void => {
            this.inProgress = false;
          }
        );
    } else {
      // Create a new site.
      this.api
        .makeRequest('post', 'v1/sites', this.site)
        .then(
          (response: any): void => {
            this.utils.showToast(
              'The ' +
                this.utils
                  .getLangTerm(
                    this.site.parent_site_id
                      ? 'child-sites.singular'
                      : 'sites.singular',
                    this.site.parent_site_id ? 'Subsite' : 'Site'
                  )
                  .toLowerCase() +
                ' was added.'
            );
            this.inProgress = false;

            if (
              this.rbac.userCanAccess([
                RBACService.getPermission(
                  RBACService.RBAC_F.F_SITES,
                  RBACService.RBAC_A.A_EDIT
                )
              ])
            ) {
              // Refresh the component to invoke change detection for the new route.
              this.router
                .navigateByUrl('/RefreshComponent', {
                  skipLocationChange: true
                })
                .then(
                  (): void => {
                    this.router.navigate([
                      '/sites',
                      response.data.id,
                      'edit',
                      'details'
                    ]);
                  }
                );
            } else {
              this.router.navigate(['/sites']);
            }
          }
        )
        .catch(
          (err: any): void => {
            this.inProgress = false;
          }
        );
    }
  }

  /**
   * Remove a single site.
   */
  onRemove(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' +
        this.utils.getLangTerm(
          this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular',
          this.site.parent_site_id ? 'Subsite' : 'Site'
        ),
      'Are you sure you want to remove this ' +
        this.utils
          .getLangTerm(
            this.site.parent_site_id
              ? 'child-sites.singular'
              : 'sites.singular',
            this.site.parent_site_id ? 'Subsite' : 'Site'
          )
          .toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest('delete', 'v1/sites/' + this.site.id)
          .then(
            (response: any): void => {
              this.utils.showToast(
                'The ' +
                  this.utils
                    .getLangTerm(
                      this.site.parent_site_id
                        ? 'child-sites.singular'
                        : 'sites.singular',
                      this.site.parent_site_id ? 'Subsite' : 'Site'
                    )
                    .toLowerCase() +
                  ' was removed.'
              );
              this.router.navigate(['/sites']);
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  /**
   * Show the site start and end dates if the status of the site is any of:
   * - Pre-Construction
   * - Under Construction
   * - Hand Over Complete
   *
   * @param site_status The status of the site.
   */
  showProjectStartEndDates(site_status: string): boolean {
    return (
      ['Pre-Construction', 'Under Construction', 'Hand Over Complete'].indexOf(
        site_status
      ) > -1
    );
  }

  /**
   * Added a new site selector to link sites with parent sites.
   */
  onSelectParentSite(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: [this.site.parent_site_id],
        multiple: false,
        allow_no_selection: true
      },
      {
        width: '1200px'
      },
      (results: any): void => {
        if (typeof results !== 'undefined') {
          this.site.parent_site_id = results;
        }
      }
    );
  }

  /**
   * Added a new tag selector to link tags with sites.
   */
  onSelectTags(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_TAGS,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      TagsSelectorComponent,
      {
        selected: this.site.tag_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]): void => {
        if (!results) {
          return;
        }
        this.site.tag_ids = results;
      }
    );
  }

  /**
   * Retrieves the details of the output start date and time based on the provided event.
   * @return {void}
   * @param estimatedProjectStartDate
   */
  getOutputStartDateTimeDetails(estimatedProjectStartDate: any): void {
    this.estimatedProjectStartDate = estimatedProjectStartDate;
  }

  /**
   * Retrieves the details of the output end date and time based on the provided event.
   * @return {void}
   * @param estimatedProjectEndDate
   */
  getOutputEndDateTimeDetails(estimatedProjectEndDate: any): void {
    this.estimatedProjectEndDate = estimatedProjectEndDate;
  }

  /**
   * Retrieves the details of the output site opening time based on the provided event.
   * @return {void}
   * @param siteOpenTime
   */
  getOutputOpeningTimeDetails(siteOpenTime: any): void {
    this.siteOpenTime = siteOpenTime;
  }

  /**
   * Retrieves the details of the output site closing time based on the provided event.
   * @return {void}
   * @param siteCloseTime
   */
  getOutputClosingTimeDetails(siteCloseTime: any): void {
    this.siteCloseTime = siteCloseTime;
  }

  /**
   * Convert the given time string in 'HH:mm:ss' format to a moment object with today's date as the carrier.
   *
   * @param {string | null} time - The time string in 'HH:mm:ss' format to convert to a moment object. Can be null.
   * @return {moment | null} A moment object with the time from the input string and today's date as the carrier, or null if the input time is invalid.
   */
  private toMoment(time: string | null): Moment | null {
    if (!time) {
      return null;
    }
    return moment(time, 'HH:mm:ss', true).isValid()
      ? moment(time, 'HH:mm:ss')
      : null;
  }

  /**
   * Convert a moment object to a string in 'HH:mm:ss' format.
   *
   * @param {Moment | null} val - The moment object to be converted to a string. Can be null.
   * @return {string | null} A string representing the time in 'HH:mm:ss' format, or null if the input is null.
   */
  private toTimeString(val: Moment | null): string | null {
    return val ? val.format('HH:mm:ss') : null;
  }

  protected readonly RBACService = RBACService;
}
